/**
 * Analytics & Insights Page
 * AI-powered analytics, reporting, and business intelligence
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { 
  BarChart3, TrendingUp, TrendingDown, Brain, Sparkles, Target,
  Users, DollarSign, Clock, Calendar, ArrowUpRight, ArrowDownRight,
  Lightbulb, AlertCircle, CheckCircle, Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock analytics data
const kpiData = {
  revenue: { value: 2450000, change: 12.5, trend: 'up' },
  orders: { value: 8420, change: 8.3, trend: 'up' },
  avgTicket: { value: 291, change: -2.1, trend: 'down' },
  customers: { value: 3250, change: 15.7, trend: 'up' },
  repeatRate: { value: 42, change: 5.2, trend: 'up' },
  laborCost: { value: 28.5, change: -1.8, trend: 'down' },
};

const topItems = [
  { name: 'Butter Chicken', sales: 1245, revenue: 234255, trend: 'up', change: 18 },
  { name: 'Chicken Tikka Masala', sales: 1120, revenue: 211680, trend: 'up', change: 12 },
  { name: 'Lamb Biryani', sales: 890, revenue: 195580, trend: 'up', change: 8 },
  { name: 'Garlic Naan', sales: 2340, revenue: 93600, trend: 'stable', change: 2 },
  { name: 'Mango Lassi', sales: 1560, revenue: 78000, trend: 'down', change: -5 },
];

const aiInsights = [
  {
    id: 1,
    type: 'opportunity',
    title: 'Øk ettermiddagssalget',
    description: 'Analysen viser 35% lavere salg mellom 14:00-17:00. Vurder happy hour eller lunsjtilbud.',
    impact: 'høy',
    potentialRevenue: 45000,
    confidence: 85,
  },
  {
    id: 2,
    type: 'warning',
    title: 'Matsvinn øker',
    description: 'Matsvinn har økt 12% siste måned. Hovedsakelig grønnsaker og meieriprodukter.',
    impact: 'medium',
    potentialSavings: 8500,
    confidence: 92,
  },
  {
    id: 3,
    type: 'success',
    title: 'Lojalitetsprogram fungerer',
    description: 'Medlemmer bruker 45% mer enn ikke-medlemmer. Gjenbesøksraten har økt 23%.',
    impact: 'høy',
    confidence: 94,
  },
  {
    id: 4,
    type: 'recommendation',
    title: 'Menyoptimalisering',
    description: 'Palak Paneer har høy margin men lav synlighet. Flytt til toppen av vegetar-kategorien.',
    impact: 'medium',
    potentialRevenue: 15000,
    confidence: 78,
  },
];

const forecastData = {
  nextWeek: { revenue: 625000, orders: 2150, confidence: 88 },
  nextMonth: { revenue: 2680000, orders: 9200, confidence: 75 },
};

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0 
    }).format(amount);
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'opportunity': return <Lightbulb className="w-5 h-5 text-spice-turmeric" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-spice-tandoori" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-spice-cardamom" />;
      case 'recommendation': return <Zap className="w-5 h-5 text-blue-500" />;
      default: return <Brain className="w-5 h-5 text-brand-orange" />;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'opportunity': return 'border-l-spice-turmeric bg-spice-turmeric/5';
      case 'warning': return 'border-l-spice-tandoori bg-spice-tandoori/5';
      case 'success': return 'border-l-spice-cardamom bg-spice-cardamom/5';
      case 'recommendation': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-brand-orange bg-brand-orange/5';
    }
  };

  return (
    <div data-testid="analytics-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-brand-charcoal flex items-center gap-3">
            Analytics & Innsikt
            <Badge className="bg-gradient-to-r from-brand-orange to-spice-turmeric gap-1">
              <Sparkles className="w-3 h-3" /> AI-drevet
            </Badge>
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Innsikt og rapportering drevet av maskinlæring
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Denne uken</SelectItem>
              <SelectItem value="month">Denne måneden</SelectItem>
              <SelectItem value="quarter">Dette kvartalet</SelectItem>
              <SelectItem value="year">Dette året</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2">
            <Calendar className="w-4 h-4" />
            Egendefinert
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase">Omsetning</span>
              <DollarSign className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-brand-charcoal">{formatCurrency(kpiData.revenue.value)}</p>
            <div className={cn(
              "flex items-center gap-1 text-xs mt-1",
              kpiData.revenue.trend === 'up' ? "text-spice-cardamom" : "text-spice-tandoori"
            )}>
              {kpiData.revenue.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {kpiData.revenue.change}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase">Ordrer</span>
              <BarChart3 className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-brand-charcoal">{kpiData.orders.value.toLocaleString()}</p>
            <div className={cn(
              "flex items-center gap-1 text-xs mt-1",
              kpiData.orders.trend === 'up' ? "text-spice-cardamom" : "text-spice-tandoori"
            )}>
              {kpiData.orders.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {kpiData.orders.change}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase">Snittbong</span>
              <Target className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-brand-charcoal">{formatCurrency(kpiData.avgTicket.value)}</p>
            <div className={cn(
              "flex items-center gap-1 text-xs mt-1",
              kpiData.avgTicket.trend === 'up' ? "text-spice-cardamom" : "text-spice-tandoori"
            )}>
              {kpiData.avgTicket.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(kpiData.avgTicket.change)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase">Kunder</span>
              <Users className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-brand-charcoal">{kpiData.customers.value.toLocaleString()}</p>
            <div className={cn(
              "flex items-center gap-1 text-xs mt-1",
              kpiData.customers.trend === 'up' ? "text-spice-cardamom" : "text-spice-tandoori"
            )}>
              {kpiData.customers.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {kpiData.customers.change}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase">Gjenbesøk</span>
              <TrendingUp className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-brand-charcoal">{kpiData.repeatRate.value}%</p>
            <div className={cn(
              "flex items-center gap-1 text-xs mt-1",
              kpiData.repeatRate.trend === 'up' ? "text-spice-cardamom" : "text-spice-tandoori"
            )}>
              {kpiData.repeatRate.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {kpiData.repeatRate.change}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase">Lønnskost</span>
              <Clock className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-brand-charcoal">{kpiData.laborCost.value}%</p>
            <div className={cn(
              "flex items-center gap-1 text-xs mt-1",
              kpiData.laborCost.trend === 'down' ? "text-spice-cardamom" : "text-spice-tandoori"
            )}>
              {kpiData.laborCost.trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
              {Math.abs(kpiData.laborCost.change)}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Oversikt
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2">
            <Brain className="w-4 h-4" />
            AI-innsikt
            <Badge className="ml-1 bg-brand-orange text-[10px]">{aiInsights.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="forecast" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Prognoser
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Top Selling Items */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Toppsalg</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topItems.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white",
                          index === 0 ? "bg-yellow-500" :
                          index === 1 ? "bg-gray-400" :
                          index === 2 ? "bg-amber-700" :
                          "bg-gray-300"
                        )}>
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.sales} solgt</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(item.revenue)}</p>
                        <div className={cn(
                          "flex items-center gap-1 text-xs justify-end",
                          item.trend === 'up' ? "text-spice-cardamom" :
                          item.trend === 'down' ? "text-spice-tandoori" :
                          "text-gray-500"
                        )}>
                          {item.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> :
                           item.trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : null}
                          {item.change > 0 ? '+' : ''}{item.change}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Performance Gauge */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Måloppnåelse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Omsetning vs mål</span>
                    <span className="text-sm font-bold">92%</span>
                  </div>
                  <Progress value={92} className="h-3 [&>div]:bg-spice-cardamom" />
                  <p className="text-xs text-gray-500 mt-1">NOK 2.45M av NOK 2.66M mål</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Kundetilfredshet</span>
                    <span className="text-sm font-bold">4.6/5</span>
                  </div>
                  <Progress value={92} className="h-3 [&>div]:bg-spice-cardamom" />
                  <p className="text-xs text-gray-500 mt-1">Basert på 342 anmeldelser</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Matsvinn</span>
                    <span className="text-sm font-bold">3.2%</span>
                  </div>
                  <Progress value={68} className="h-3 [&>div]:bg-spice-turmeric" />
                  <p className="text-xs text-gray-500 mt-1">Mål: under 2.5%</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Personalomsetning</span>
                    <span className="text-sm font-bold">8%</span>
                  </div>
                  <Progress value={80} className="h-3 [&>div]:bg-spice-cardamom" />
                  <p className="text-xs text-gray-500 mt-1">Under bransjesnitt (12%)</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          <div className="flex items-center gap-2 p-4 bg-gradient-to-r from-brand-orange/10 to-spice-turmeric/10 rounded-lg">
            <Brain className="w-6 h-6 text-brand-orange" />
            <div>
              <p className="font-medium text-brand-charcoal">AI-analyse oppdatert</p>
              <p className="text-sm text-gray-500">Sist analysert: for 2 timer siden • Neste oppdatering: om 4 timer</p>
            </div>
          </div>

          <div className="grid gap-4">
            {aiInsights.map(insight => (
              <Card key={insight.id} className={cn("border-l-4", getInsightColor(insight.type))}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getInsightIcon(insight.type)}
                      <div>
                        <h3 className="font-medium text-brand-charcoal">{insight.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {insight.impact === 'høy' ? '🔴' : '🟡'} {insight.impact} påvirkning
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {insight.confidence}% sikkerhet
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {insight.potentialRevenue && (
                        <p className="text-lg font-bold text-spice-cardamom">
                          +{formatCurrency(insight.potentialRevenue)}
                        </p>
                      )}
                      {insight.potentialSavings && (
                        <p className="text-lg font-bold text-spice-cardamom">
                          -{formatCurrency(insight.potentialSavings)}
                        </p>
                      )}
                      <Button size="sm" className="mt-2 bg-brand-orange hover:bg-brand-orange/90">
                        Se detaljer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-brand-orange" />
                  Prognose neste uke
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Forventet omsetning</p>
                    <p className="text-3xl font-bold text-brand-charcoal">{formatCurrency(forecastData.nextWeek.revenue)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Progress value={forecastData.nextWeek.confidence} className="flex-1 h-2" />
                      <span className="text-xs text-gray-500">{forecastData.nextWeek.confidence}% sikkerhet</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Forventet antall ordrer</p>
                    <p className="text-3xl font-bold text-brand-charcoal">{forecastData.nextWeek.orders.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-brand-orange" />
                  Prognose neste måned
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Forventet omsetning</p>
                    <p className="text-3xl font-bold text-brand-charcoal">{formatCurrency(forecastData.nextMonth.revenue)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Progress value={forecastData.nextMonth.confidence} className="flex-1 h-2" />
                      <span className="text-xs text-gray-500">{forecastData.nextMonth.confidence}% sikkerhet</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Forventet antall ordrer</p>
                    <p className="text-3xl font-bold text-brand-charcoal">{forecastData.nextMonth.orders.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
